import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner } from '@radix-ui/themes';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckboxGroupComponent from '../../../components/inputs/CheckboxGroup/CheckboxGroupComponent';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import { useGetClaims } from '../../../hooks/useGetClaims';
import { useGetRoleClaims } from '../../../hooks/useGetRoleClaims';
import { useGetRolesQuery } from '../../../hooks/useGetRolesQuery';
import { useGetUserClaims } from '../../../hooks/useGetUserClaims';
import { useGetUserRoles } from '../../../hooks/useGetUserRoles';
import { RootState } from '../../../redux/store';
import { routes } from '../../../routes';
import { addUserClaims } from '../../../services/user/addUserClaims';
import { addUserRole } from '../../../services/user/addUserRole';
import { removeUserClaims } from '../../../services/user/removeUserClaims';
import { removeUserRole } from '../../../services/user/removeUserRole';
import { GetClaimsToRoleType } from '../../../types/Role';
import { translations } from '../../../utils/claims';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isEmpty } from 'lodash';

type FormValues = {
  userId: string,
  fullname?: string,
  roleId: string,
  claims?: string[],
}



export const UserRoleAndClaims = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const {roles: userRoles, claims:userClaims } =  user;
  const defaultValues: FormValues = {
    fullname: user?.name + ' ' + user?.userName || '',
    userId: user.id ?? '',
    claims: userClaims?.map((uc: GetClaimsToRoleType) => uc.value) ?? [],
    roleId: userRoles?.at(0)?.name ?? ""
  }
   const UserRoleFormValidation = yup.object().shape({
    userId: yup.string().required('Utilizador é obrigatório'),
    roleId: yup.string().required('Perfil é obrigatório'),
  });
  const { control, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm<FormValues>({
    resolver: yupResolver(UserRoleFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,

  });

  const { data: roles, isLoading: isLoadingRoles, isError: isErrorRoles} = useGetRolesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: claims, isLoading: claimsLoading, isError: claimsError } = useGetClaims();
  const { data: roleClaims, refetch: refetchRoleClaims } = useGetRoleClaims(getValues('roleId'), {
    enabled: !!getValues('roleId'),
  })

  const handleRoleChange = (roleId: string) => {
    setValue('roleId', roleId);
      refetchRoleClaims();
  };

  const { mutateAsync: mutationAddUserClaims, isLoading: isLoadingMutationAddUserClaims } = useMutation({ mutationKey: "mutationAddUserClaims", mutationFn: addUserClaims });
  const { mutateAsync: mutationRemoveUserClaims, isLoading: isLoadingMutationRemoveUserClaims } = useMutation({ mutationKey: "mutationRemoveUserClaims", mutationFn: removeUserClaims });
  const { mutateAsync: mutationRemoveUserRole, isLoading: isLoadingMutationRemoveUserRole } = useMutation({ mutationKey: "removeUserRole", mutationFn: removeUserRole });
  const { mutateAsync: mutationAddUserRole, isLoading: isLoadingMutationAddUserRole } = useMutation({ mutationKey: "addUserRole", mutationFn: addUserRole });

  const handleBack = () => {
    navigate(routes.users);
  };

  const handleSubmitRoles = async (request: any) => {
    try {
      const currentRoleId = userRoles?.[0]?.name;
      if (request.roleId !== currentRoleId && !isEmpty(currentRoleId)) {
         await mutationRemoveUserRole({
          roleId: currentRoleId ?? "",
          userId: user.id ?? ''
        });
      }
      await mutationAddUserRole({
        roleId: request.roleId,
        userId: user.id ?? ''
      });

      return true;
    } catch (error) {
      return false;
    }

  }

  const handleSubmitClaims = async (request: any) => {

    try {

      if (request.claims.length === 0 && userClaims?.length === 0) {
        return true;
      }
      const currentClaims = userClaims?.map((uc: GetClaimsToRoleType) => uc.value) || [];
      const submittedClaims = request.claims;
      const claimsFromRole = roleClaims?.map(rc => rc.value) ?? [];

      const commonClaims = currentClaims.filter(claim => claimsFromRole.includes(claim));
      if (commonClaims.length > 0) {
        await mutationRemoveUserClaims({ claims: commonClaims, userId: user.id ?? '' });
      }

      const claimsAdded = submittedClaims.filter((claim: string) => !currentClaims.includes(claim));
      const claimsRemoved = currentClaims.filter(claim => !submittedClaims.includes(claim));

      if (claimsRemoved.length > 0) {
        await mutationRemoveUserClaims({ claims: claimsRemoved, userId: user.id ?? '' });
      }

      if (claimsAdded.length > 0) {
        await mutationAddUserClaims({ claims: claimsAdded, userId: user.id ?? '' });
      }
      return true;
    } catch (error) {
      return false;
    }

  }

  const onSubmit = handleSubmit(async (request) => {
    const isRolesDone = await handleSubmitRoles(request);
    const isClaimsDone = await handleSubmitClaims(request);
    if (isRolesDone && isClaimsDone) {
      toast.success(`Perfil e as permissões do utilizador ${user.name} actualizadas`)
      handleBack();
    } else {
      toast.error(`Ocorreu um erro ao actualizar perfil e as permissões do utilizador ${user.name}`);
    }
  });

  return (
    <Card size='3'>
      <Flex direction='column' gap='4'>
        <form onSubmit={onSubmit} noValidate>
          <Flex direction='column' gap='4'>
            <Flex gap='8'>
              <SelectComponent 
              name="userId" 
              label="Utilizador"
              control={control} 
              errors={errors}
              style={{ width: '100%' }}
              options={[{userId:user.id, fullname:user.userName }].map(_user=>{
                  return {
                    value: _user.userId!, 
                    label:_user.fullname!
                  }
                })}
              />

              <SelectComponent name="roleId" label="Perfil" control={control} errors={errors} style={{ width: '100%' }}
                options={roles?.items.map(role => ({
                  value: role.name ?? "",
                  label: role.name ?? ""
                }))}
                onSelect={handleRoleChange}
                itemsError={isErrorRoles}
                itemsLoading= {isLoadingRoles}
              />
            </Flex>

            <CheckboxGroupComponent
              name="claims"
              label="Permissões"
              control={control}
              errors={errors}
              options={claims && claims.filter(claim => !roleClaims?.map(rc => rc.value)?.includes(claim)).map(claim => ({ value: claim, label: translations[claim as keyof typeof translations] || claim }))}
              style={{ width: '100%' }}
              itemsError={claimsError}
              itemsLoading={claimsLoading}
            />

            <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
              <Button type="button" variant='soft' color='gray' onClick={handleBack} disabled={isLoadingMutationAddUserClaims || isLoadingMutationRemoveUserClaims || isLoadingMutationRemoveUserRole || isLoadingMutationAddUserRole}>
                <ChevronLeftIcon /> Voltar
              </Button>
              <Button type="submit" disabled={isLoadingMutationAddUserClaims || isLoadingMutationRemoveUserClaims || isLoadingMutationRemoveUserRole || isLoadingMutationAddUserRole} variant='surface'>
                {isLoadingMutationAddUserClaims || isLoadingMutationRemoveUserClaims || isLoadingMutationRemoveUserRole || isLoadingMutationAddUserRole ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
                Guardar
              </Button>
            </Flex>

          </Flex>
        </form>
      </Flex>
    </Card>
  );

}